import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BellIcon, ChevronDownIcon, MenuIcon, SearchIcon } from 'lucide-react';
import { notifications as adminNotifications } from '../../data/org';
import { SELF_ID, selfNotifications } from '../../data/selfService';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useHRStore } from '../../hooks/useHRStore';
import type { Navigate } from '../../types/hr';
import type { AppView } from '../../types/selfService';
import { displayName } from '../../utils/employees';
import { Avatar } from '../ui/Avatar';
import { ViewSwitch } from './ViewSwitch';
interface TopBarProps {
  view: AppView;
  onViewChange: (view: AppView) => void;
  onNavigate: Navigate;
  onOpenMenu: () => void;
}
export function TopBar({
  view,
  onViewChange,
  onNavigate,
  onOpenMenu
}: TopBarProps) {
  const {
    employees
  } = useHRStore();
  const isAdmin = view === 'admin';
  const me = employees.find(e => e.id === SELF_ID);
  const notifications = isAdmin ? adminNotifications : selfNotifications;
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [readAll, setReadAll] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setReadAll(false);
    setBellOpen(false);
    setQuery('');
  }, [view]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const closeBell = useCallback(() => setBellOpen(false), []);
  useClickOutside(searchRef, closeSearch, searchOpen);
  useClickOutside(bellRef, closeBell, bellOpen);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return employees.filter(e => `${displayName(e)} ${e.jobTitle} ${e.department} ${e.employeeNumber}`.toLowerCase().includes(q)).slice(0, 6);
  }, [query, employees]);
  const unread = readAll ? 0 : notifications.filter(n => n.unread).length;
  const openEmployee = (id: string) => {
    onNavigate({
      page: 'profile',
      employeeId: id
    });
    setQuery('');
    setSearchOpen(false);
  };
  return <header className="sticky top-0 z-20 border-b border-slate-200/70 bg-white/85 backdrop-blur-md" data-id="element-195" data-mp="d2o7t6u2j9h3">
      <div className="flex h-16 items-center gap-4 px-6 lg:px-10" data-id="element-196" data-mp="h385cbik0f3r">
        <button type="button" onClick={onOpenMenu} className="rounded-lg p-2 text-navy hover:bg-slate-100 lg:hidden" aria-label="Open navigation" data-id="element-197" data-mp="m3m1owasm0ly">
          <MenuIcon className="h-5 w-5" data-id="element-198" data-mp="qztacwukpq7l" />
        </button>

        {!isAdmin && <p className="hidden min-w-0 truncate text-sm text-muted md:block" data-id="element-199" data-mp="117fcyyt7lx3y">
            Welcome back, <span className="font-semibold text-navy" data-id="element-200" data-mp="w56i1wws0kz7">{me ? me.firstName : 'there'}</span> — you're viewing your own information only.
          </p>}

        <div ref={searchRef} className={`relative w-full max-w-md ${isAdmin ? '' : 'hidden'}`} data-id="element-201" data-mp="n6cs2pdyasum">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-202" data-mp="1jw2h9f3kaigh" />
          <input ref={inputRef} type="search" value={query} onChange={e => {
          setQuery(e.target.value);
          setSearchOpen(true);
        }} onFocus={() => setSearchOpen(true)} onKeyDown={e => {
          if (e.key === 'Enter' && results[0]) openEmployee(results[0].id);
        }} placeholder="Search people, employee #, departments…" aria-label="Global search" className="h-10 w-full rounded-xl border-0 bg-canvas pl-10 pr-14 text-sm text-navy ring-1 ring-inset ring-slate-200/80 placeholder:text-mutedlight focus:bg-white focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-203" data-mp="1eyk41ciihh2o" />
          <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-md bg-white px-1.5 py-0.5 text-[10px] font-medium text-muted ring-1 ring-slate-200 sm:block" data-id="element-204" data-mp="19fi2h251diub">
            ⌘K
          </kbd>
          <AnimatePresence data-id="element-205" data-mp="263elseruys98">
            {searchOpen && query.trim() && <motion.div initial={{
            opacity: 0,
            y: -4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -4
          }} transition={{
            duration: 0.12
          }} className="absolute left-0 right-0 top-12 overflow-hidden rounded-2xl bg-white py-2 shadow-lift ring-1 ring-slate-900/5" data-id="element-206" data-mp="265ipavluhp8z">
                {results.length === 0 ? <p className="px-4 py-3 text-sm text-muted" data-id="element-207" data-mp="df6wkc3fk7u7">No people match “{query}”.</p> : <ul aria-label="Search results" data-id="element-208" data-mp="8gnbsf0kp23f">
                    {results.map(e => <li key={e.id} data-id="element-209" data-mp="n5jhi5f5q30v">
                        <button type="button" onClick={() => openEmployee(e.id)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-canvas" data-id="element-210" data-mp="n4grs0tkm6mj">
                          <Avatar name={displayName(e)} seed={e.id} size="sm" data-id="element-211" data-mp="3qkxcv6iyt2pe" />
                          <span className="min-w-0 flex-1" data-id="element-212" data-mp="3lmvsmi9syxcc">
                            <span className="block truncate text-sm font-medium text-navy" data-id="element-213" data-mp="3logpwfni4udw">{displayName(e)}</span>
                            <span className="block truncate text-xs text-muted" data-id="element-214" data-mp="4c5uqnlll2wc">
                              {e.jobTitle} · {e.department}
                            </span>
                          </span>
                          <span className="text-xs text-mutedlight" data-id="element-215" data-mp="1aukw6ael2ael">{e.employeeNumber}</span>
                        </button>
                      </li>)}
                  </ul>}
              </motion.div>}
          </AnimatePresence>
        </div>

        <div className="ml-auto flex items-center gap-2" data-id="element-216" data-mp="1myov77b3tcmc">
          <ViewSwitch view={view} onChange={onViewChange} data-id="element-217" data-mp="1rx9v15mg1ua9" />
          <div ref={bellRef} className="relative" data-id="element-218" data-mp="1wurum5ekmx5l">
            <button type="button" onClick={() => setBellOpen(o => !o)} className="relative flex h-10 w-10 items-center justify-center rounded-xl text-navy transition hover:bg-canvas" aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`} aria-expanded={bellOpen} data-id="element-219" data-mp="1wua29jv1532y">
              <BellIcon className="h-5 w-5" data-id="element-220" data-mp="1dwfjwf80hf7w" />
              {unread > 0 && <span className="absolute right-2.5 top-2.5 h-2 w-2 rounded-full bg-flame ring-2 ring-white" data-id="element-221" data-mp="1dy0h6clpnc9g" />}
            </button>
            <AnimatePresence data-id="element-222" data-mp="lfcx90iga39y">
              {bellOpen && <motion.div initial={{
              opacity: 0,
              y: -4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} transition={{
              duration: 0.12
            }} className="absolute right-0 top-12 w-80 overflow-hidden rounded-2xl bg-white shadow-lift ring-1 ring-slate-900/5" data-id="element-223" data-mp="lds7do72z8if">
                  <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3" data-id="element-224" data-mp="2kpv78le93u2u">
                    <p className="text-sm font-semibold text-navy" data-id="element-225" data-mp="2fsla0jkmee4g">Notifications</p>
                    <button type="button" onClick={() => setReadAll(true)} className="text-xs font-medium text-azure hover:underline" data-id="element-226" data-mp="2azs803gjdjw2">
                      Mark all read
                    </button>
                  </div>
                  <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto" data-id="element-227" data-mp="21xso93e501pf">
                    {notifications.map(n => <li key={n.id} data-id="element-228" data-mp="1s1zbbiwia674">
                        <button type="button" onClick={() => {
                    setBellOpen(false);
                    onNavigate(n.route);
                  }} className="flex w-full gap-3 px-4 py-3 text-left transition hover:bg-canvas" data-id="element-229" data-mp="1s30te7bxvsyc">
                          <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.unread && !readAll ? 'bg-flame' : 'bg-slate-200'}`} data-id="element-230" data-mp="3bw0vhv8fgr47" />
                          <span className="min-w-0" data-id="element-231" data-mp="2x6mk6hlajzm6">
                            <span className="block text-sm font-medium text-navy" data-id="element-232" data-mp="31z603l7gb6u5">{n.title}</span>
                            <span className="mt-0.5 block text-xs leading-relaxed text-muted" data-id="element-233" data-mp="2o7rd73afalht">{n.body}</span>
                            <span className="mt-1 block text-[11px] text-mutedlight" data-id="element-234" data-mp="2t5j722umohon">{n.time}</span>
                          </span>
                        </button>
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>

          <div className="mx-1 hidden h-8 w-px bg-slate-200 sm:block" data-id="element-235" data-mp="1zn4kdzjgflby" />

          <button type="button" onClick={() => onNavigate(isAdmin ? {
          page: 'profile',
          employeeId: 'e1'
        } : {
          page: 'my-info'
        })} aria-label="Open my profile" className="flex items-center gap-3 rounded-xl py-1.5 pl-1.5 pr-3 transition hover:bg-canvas" data-id="element-236" data-mp="1vlj9ukue7j4x">
            <Avatar name={isAdmin ? 'Jordan Avery' : 'Jordan Sample'} seed={isAdmin ? 'e1' : SELF_ID} size="sm" data-id="element-237" data-mp="2ik5qjnoqvrhh" />
            <span className="hidden text-left xl:block" data-id="element-238" data-mp="2ej3zhu4zelql">
              <span className="block text-sm font-medium leading-tight text-navy" data-id="element-239" data-mp="2sbjuc7nqbdgf">{isAdmin ? 'Jordan Avery' : 'Jordan Sample'}</span>
              <span className="block text-[11px] leading-tight text-muted" data-id="element-240" data-mp="2nd8ixshscuw3">{isAdmin ? 'HR Admin' : me?.jobTitle ?? 'Employee'}</span>
            </span>
            <ChevronDownIcon className="hidden h-4 w-4 text-muted sm:block" aria-hidden="true" data-id="element-241" data-mp="2xa3k6j5g19e2" />
          </button>
        </div>
      </div>
    </header>;
}